import { useState } from 'react';
import { useSessionParticipants, SessionParticipant } from '@/hooks/useSessionParticipants';
import { useWorkspaceMembers } from '@/hooks/useWorkspaceMembers';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';
import { Crown, Shield, Eye, UserPlus, Trash2, Users, Loader2, Pencil, Info } from 'lucide-react';

interface ParticipantsPanelProps {
  sessionId: string;
  workspaceId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const roleConfig: Record<string, { label: string; icon: React.ReactNode; color: string; permissions: string[] }> = {
  owner: {
    label: 'Owner',
    icon: <Crown className="h-3 w-3" />,
    color: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20',
    permissions: ['Rollen ändern & Teilnehmer verwalten', 'Entscheidung committen & abschließen', 'Reports erzeugen & freigeben', 'Alle Inhalte bearbeiten'],
  },
  contributor: {
    label: 'Contributor',
    icon: <Pencil className="h-3 w-3" />,
    color: 'bg-primary/10 text-primary border-primary/20',
    permissions: ['Eigene Antworten & Optionen bearbeiten', 'Szenarien erstellen & bewerten', 'Matrix-Einträge hinzufügen'],
  },
  reviewer: {
    label: 'Reviewer',
    icon: <Shield className="h-3 w-3" />,
    color: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20',
    permissions: ['Kommentare schreiben', 'Inhalte lesen & prüfen', 'Keine Bearbeitungsrechte'],
  },
  viewer: {
    label: 'Viewer',
    icon: <Eye className="h-3 w-3" />,
    color: 'bg-muted text-muted-foreground border-border',
    permissions: ['Nur lesen', 'Keine Bearbeitungs- oder Kommentarrechte'],
  },
};

function getInitials(name: string | null, email: string): string {
  if (name) {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  }
  return email.slice(0, 2).toUpperCase();
}

export function ParticipantsPanel({ sessionId, workspaceId, open, onOpenChange }: ParticipantsPanelProps) {
  const { toast } = useToast();
  const { participants, isOwner, loading, addParticipant, updateRole, removeParticipant } = useSessionParticipants(sessionId);
  const { members: workspaceMembers, loading: membersLoading } = useWorkspaceMembers(workspaceId);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedRole, setSelectedRole] = useState<'contributor' | 'reviewer' | 'viewer'>('contributor');
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Workspace members not yet in this session
  const availableMembers = workspaceMembers.filter(
    m => m.user_id && m.seat_status === 'active' && !participants.some(p => p.user_id === m.user_id)
  );

  const handleAdd = async () => {
    if (!selectedUserId) return;
    setActionLoading('add');
    try {
      await addParticipant(selectedUserId, selectedRole);
      toast({ title: 'Teilnehmer hinzugefügt' });
      setShowAddForm(false);
      setSelectedUserId('');
    } catch (err: any) {
      toast({ title: 'Fehler', description: err.message, variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleRoleChange = async (participant: SessionParticipant, newRole: string) => {
    if (newRole === 'owner' || participant.role === 'owner') return;
    setActionLoading(participant.id);
    try {
      await updateRole(participant.id, newRole as 'contributor' | 'reviewer' | 'viewer');
      toast({ title: 'Rolle geändert' });
    } catch (err: any) {
      toast({ title: 'Fehler', description: err.message, variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemove = async (participant: SessionParticipant) => {
    if (participant.role === 'owner') return;
    setActionLoading(participant.id);
    try {
      await removeParticipant(participant.id);
      toast({ title: 'Teilnehmer entfernt' });
    } catch (err: any) {
      toast({ title: 'Fehler', description: err.message, variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  const owner = participants.find(p => p.role === 'owner');

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:w-[400px] md:w-[440px]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Teilnehmer ({participants.length})
          </SheetTitle>
          <SheetDescription>
            {owner && (
              <span className="flex items-center gap-1.5">
                <Crown className="h-3.5 w-3.5 text-amber-500" />
                Owner: <strong>{owner.name || owner.email}</strong>
              </span>
            )}
          </SheetDescription>
        </SheetHeader>

        {/* Role Explainer */}
        <div className="mt-4 p-3 rounded-xl border border-border/50 bg-muted/30 space-y-2">
          <div className="flex items-center gap-1.5">
            <Info className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-xs font-medium text-muted-foreground">Rollen & Berechtigungen</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(roleConfig).map(([key, config]) => (
              <Tooltip key={key}>
                <TooltipTrigger asChild>
                  <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg border text-[11px] cursor-help ${config.color}`}>
                    {config.icon}
                    <span className="font-medium">{config.label}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[220px]">
                  <p className="font-semibold text-xs mb-1">{config.label}</p>
                  <ul className="space-y-0.5">
                    {config.permissions.map((perm, i) => (
                      <li key={i} className="text-[11px] text-muted-foreground flex items-start gap-1">
                        <span className="text-primary mt-0.5">•</span>
                        {perm}
                      </li>
                    ))}
                  </ul>
                </TooltipContent>
              </Tooltip>
            ))}
          </div>
        </div>

        <div className="mt-4 space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            participants.map(participant => {
              const config = roleConfig[participant.role] || roleConfig.viewer;
              const isActionTarget = actionLoading === participant.id;

              return (
                <div key={participant.id} className="flex items-center gap-3 p-3 rounded-xl border border-border/50 bg-card hover:bg-muted/30 transition-colors">
                  <Avatar className="h-9 w-9">
                    <AvatarImage src={participant.avatar_url || undefined} />
                    <AvatarFallback className="text-xs bg-primary/10 text-primary">
                      {getInitials(participant.name, participant.email)}
                    </AvatarFallback>
                  </Avatar>

                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {participant.name || participant.email}
                    </p>
                    {participant.name && (
                      <p className="text-[11px] text-muted-foreground truncate">{participant.email}</p>
                    )}
                  </div>

                  {/* Role badge / selector */}
                  {isOwner && participant.role !== 'owner' ? (
                    <Select
                      value={participant.role}
                      onValueChange={(val) => handleRoleChange(participant, val)}
                      disabled={isActionTarget}
                    >
                      <SelectTrigger className="w-[120px] h-7 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="contributor">Contributor</SelectItem>
                        <SelectItem value="reviewer">Reviewer</SelectItem>
                        <SelectItem value="viewer">Viewer</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge variant="outline" className={`text-[10px] gap-1 cursor-help ${config.color}`}>
                          {config.icon}
                          {config.label}
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent side="left" className="max-w-[200px]">
                        <p className="font-semibold text-xs mb-1">{config.label}</p>
                        <ul className="space-y-0.5">
                          {config.permissions.map((perm, i) => (
                            <li key={i} className="text-[11px] text-muted-foreground flex items-start gap-1">
                              <span className="text-primary mt-0.5">•</span>
                              {perm}
                            </li>
                          ))}
                        </ul>
                      </TooltipContent>
                    </Tooltip>
                  )}

                  {/* Remove button (owner only, not for owner role) */}
                  {isOwner && participant.role !== 'owner' && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                      onClick={() => handleRemove(participant)}
                      disabled={isActionTarget}
                    >
                      {isActionTarget ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Add participant section (owner only) */}
        {isOwner && (
          <>
            <Separator className="my-4" />
            {showAddForm ? (
              <div className="space-y-3 p-3 rounded-xl border border-primary/20 bg-primary/5">
                <p className="text-sm font-medium text-foreground">Teilnehmer hinzufügen</p>
                {membersLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                ) : availableMembers.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Alle Workspace-Mitglieder sind bereits Teilnehmer.</p>
                ) : (
                  <>
                    <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                      <SelectTrigger className="h-9 text-sm">
                        <SelectValue placeholder="Mitglied wählen..." />
                      </SelectTrigger>
                      <SelectContent>
                        {availableMembers.map(m => (
                          <SelectItem key={m.user_id!} value={m.user_id!}>
                            {m.profiles?.name || m.profiles?.email || m.user_id}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Select value={selectedRole} onValueChange={(v) => setSelectedRole(v as any)}>
                      <SelectTrigger className="h-9 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="contributor">Contributor</SelectItem>
                        <SelectItem value="reviewer">Reviewer</SelectItem>
                        <SelectItem value="viewer">Viewer</SelectItem>
                      </SelectContent>
                    </Select>

                    <div className="flex gap-2">
                      <Button size="sm" onClick={handleAdd} disabled={!selectedUserId || actionLoading === 'add'}>
                        {actionLoading === 'add' ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <UserPlus className="h-3.5 w-3.5 mr-1.5" />}
                        Hinzufügen
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setShowAddForm(false)}>
                        Abbrechen
                      </Button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="w-full gap-2"
                onClick={() => setShowAddForm(true)}
              >
                <UserPlus className="h-4 w-4" />
                Teilnehmer hinzufügen
              </Button>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
